import React, { useEffect, useRef, useState, type RefObject } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { saveTesterShots } from '@/modules/test-module-chitiet/tester/shotStore'
import {
  buildVisualTesterUrl,
  getVisualTester,
  UI_TESTER_QUERY,
  type VisualTesterDef,
} from '@/modules/test-module-chitiet/tester/visualTestRegistry'
import { TEST_MODULE_CHITIET_ROUTE_PATH } from '@/modules/test-module-chitiet/constants'
import { UiTestRunBanner } from '@/modules/ui-test/UiTestRunBanner'
import { UiTestRunProvider, useUiTestRun } from '@/modules/ui-test/UiTestRunContext'
import {
  advanceUiTestRunAll,
  clearUiTestReturnPath,
  finalizeUiTestRunAll,
  isUiTestRunAllActive,
  readUiTestReturnPath,
  UI_TEST_SHOW_RESULT_ALL_QUERY,
  UI_TEST_SHOW_RESULT_QUERY,
  writeUiTestResult,
  writeUiTestRunAllResult,
} from '@/modules/ui-test/uiTestSession'
import { waitFor } from '@/modules/ui-test/utils/uiTestDomActions'
import type { UiTestRunCompletePayload, UiTestScenarioId, UiTestScenarioSummary } from '@/modules/ui-test/uiTest.types'

export { UI_TESTER_QUERY, buildVisualTesterUrl, buildNhomHangVisualTesterUrl } from '@/modules/test-module-chitiet/tester/visualTestRegistry'

type ExecutorProps = {
  def: VisualTesterDef
  onDone: () => void
}

const VisualTesterExecutor: React.FC<ExecutorProps> = ({ def, onDone }) => {
  const uiTest = useUiTestRun()
  const uiTestRef = useRef(uiTest)
  uiTestRef.current = uiTest
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone
  const defRef = useRef(def)
  defRef.current = def

  useEffect(() => {
    let cancelled = false
    let hardStop = 0
    const scenarioId = defRef.current.scenarioId
    const label = defRef.current.label

    const finish = (payload: UiTestRunCompletePayload) => {
      if (cancelled) return
      cancelled = true
      const runner = uiTestRef.current

      if (isUiTestRunAllActive()) {
        const next = advanceUiTestRunAll(payload)
        if (next) {
          // Giữ host alive — URL mới sẽ đổi sessionId / remount Executor.
          runner.navigate(buildVisualTesterUrl(next), { replace: true })
          return
        }
        writeUiTestRunAllResult(finalizeUiTestRunAll())
        const returnPath = readUiTestReturnPath(TEST_MODULE_CHITIET_ROUTE_PATH)
        clearUiTestReturnPath()
        const qs = new URLSearchParams({ [UI_TEST_SHOW_RESULT_ALL_QUERY]: '1' })
        runner.navigate(`${returnPath}?${qs.toString()}`, { replace: true })
        onDoneRef.current()
        return
      }

      const returnPath = readUiTestReturnPath(TEST_MODULE_CHITIET_ROUTE_PATH)
      clearUiTestReturnPath()
      writeUiTestResult(payload)
      const qs = new URLSearchParams({ [UI_TEST_SHOW_RESULT_QUERY]: '1' })
      runner.navigate(`${returnPath}?${qs.toString()}`, { replace: true })
      onDoneRef.current()
    }

    const failSummary = (message: string): UiTestScenarioSummary => ({
      ok: false,
      passed: 0,
      skipped: 0,
      failed: 1,
      total: 1,
      features: [],
      message,
    })

    const run = async () => {
      try {
        uiTestRef.current.setProgress({
          done: 0,
          total: defRef.current.featureCount,
          message: `Khởi động Test UI ${label}…`,
        })
        try {
          await waitFor(() => Boolean(uiTestRef.current.screenRootRef.current), 5000, 80)
        } catch {
          await waitFor(() => Boolean(document.querySelector('main table, main [role="table"]')), 10_000, 100)
        }
        if (cancelled) return
        const result = await defRef.current.run(uiTestRef.current)
        if (cancelled) return
        const shotKey = `${scenarioId}-${Date.now()}`
        saveTesterShots(shotKey, result.shots)
        finish({
          summary: result.summary,
          logs: uiTestRef.current.getLogs(),
          scenarioId,
          shotKey,
        })
      } catch (e) {
        if (cancelled) return
        const message = (e as { message?: string })?.message || `Tester ${label} thất bại`
        finish({
          summary: failSummary(message),
          logs: uiTestRef.current.getLogs(),
          scenarioId,
          errorMessage: message,
        })
      } finally {
        window.clearTimeout(hardStop)
      }
    }

    const startTimer = window.setTimeout(() => {
      if (cancelled) return
      hardStop = window.setTimeout(() => {
        finish({
          summary: failSummary(`Tester ${label} quá thời gian (hard timeout 3 phút)`),
          logs: uiTestRef.current.getLogs(),
          scenarioId,
          errorMessage: `Tester ${label} quá thời gian (hard timeout 3 phút)`,
        })
      }, 180_000)
      void run()
    }, 120)

    return () => {
      cancelled = true
      window.clearTimeout(startTimer)
      window.clearTimeout(hardStop)
    }
  }, [])

  return <UiTestRunBanner />
}

/** Giữ chạy khi URL mất `uiTester` (tester tự điều hướng lọc). */
const VisualTesterHost: React.FC<{ screenRootRef: RefObject<HTMLDivElement | null> }> = ({ screenRootRef }) => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const requested = searchParams.get(UI_TESTER_QUERY)
  const requestedDef = getVisualTester(requested)
  const [sessionId, setSessionId] = useState<UiTestScenarioId | null>(null)

  useEffect(() => {
    if (requestedDef) setSessionId(requestedDef.scenarioId)
  }, [requestedDef])

  const active = sessionId ? getVisualTester(sessionId) : null
  if (!active) return null

  return (
    <UiTestRunProvider
      key={active.scenarioId}
      scenarioId={active.scenarioId}
      screenRootRef={screenRootRef}
      navigate={navigate}
    >
      <VisualTesterExecutor def={active} onDone={() => setSessionId(null)} />
    </UiTestRunProvider>
  )
}

export default VisualTesterHost
